import { splitStoredEmail } from "./messages/outboundEmail";

export const EMAIL_ATTACHMENT_LIMIT_BYTES = 10 * 1024 * 1024;

export const EMAIL_ATTACHMENT_FAILED =
  "Não foi possível enviar o e-mail com o anexo. Nada foi enviado. Tente de novo ou use um arquivo menor.";

export const EMAIL_SEND_FAILED =
  "Não foi possível enviar o e-mail. Nada foi enviado. Tente de novo.";

const EMAIL_ORIGINS = new Set([9, 30, 35]);

export function looksLikeEmailAddress(value) {
  return /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(String(value || "").trim());
}

export function parseEmailAddressList(value) {
  return String(value || "")
    .split(/\s*[,;]\s*/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function emailAddressListError(value, { label = "destinatário" } = {}) {
  const list = parseEmailAddressList(value);
  if (!list.length) return `Informe o e-mail ${label}.`;
  if (list.some((item) => !looksLikeEmailAddress(item))) {
    return list.length === 1 ? `Confira o e-mail ${label}.` : `Confira os e-mails ${label}.`;
  }
  return "";
}

export function emailAttachmentLimitError(files) {
  const list = Array.isArray(files) ? files : [];
  let total = 0;
  for (const file of list) {
    const size = Number(file?.size) || 0;
    const name = String(file?.name || "arquivo").trim() || "arquivo";
    if (size > EMAIL_ATTACHMENT_LIMIT_BYTES) {
      return `O arquivo ${name} passa de 10 MB. Envie um arquivo menor.`;
    }
    total += size;
    if (total > EMAIL_ATTACHMENT_LIMIT_BYTES) {
      return "Os anexos juntos passam de 10 MB. Envie arquivos menores.";
    }
  }
  return "";
}

export function humanizeEmailSendError(raw, { hadAttachments = false } = {}) {
  const text = String(raw || "")
    .replace(/^\s*0[12]\s*-\s*/, "")
    .trim();
  if (/passa de 10 MB|anexos juntos passam de 10 MB/i.test(text)) return text;
  if (/^Não foi possível enviar o e-mail/i.test(text)) return text;
  if (hadAttachments) return EMAIL_ATTACHMENT_FAILED;
  if (!text || /ses|mailgun|smtp|exception|status code|<\?xml|econn|timed out|invalidparameter|put s3/i.test(text)) {
    return EMAIL_SEND_FAILED;
  }
  return text;
}

export function listChatMessages(messages) {
  if (!messages) return [];
  if (typeof messages?.values === "function" && typeof messages?.size === "number") {
    return Array.from(messages.values());
  }
  return Array.isArray(messages) ? messages : [];
}

/** Só tags HTML reais. `<nome@email>` não é tag e precisa permanecer. */
const HTML_TAG_RE = /<\/?(?:[a-zA-Z][a-zA-Z0-9:-]*|!--)(?:\s[^>]*)?\/?>/g;
const INBOUND_FOOTER_RE = /caso tenha problemas para visualizar a mensagem/i;
const INBOUND_ASSUNTO_RE = /^\s*-?\s*assunto\s*:\s*(.*)$/i;
const INBOUND_ENCAMINHADO_RE = /^\s*-?\s*encaminhado pelo e-?mail\s*:\s*(.*)$/i;
const INBOUND_MENSAGEM_RE = /^\s*-?\s*mensagem\s*:\s*(.*)$/i;

/** Texto do e-mail sem tags, com quebras onde havia `<br>` ou parágrafo. */
export function plainEmailText(value) {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n")
    .replace(HTML_TAG_RE, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function subjectFromMessageMeta(message) {
  const tags = message?.emailOriginalTags;
  const tagged =
    (tags && typeof tags === "object" && (tags.Subject || tags.subject)) ||
    message?.emailSubject ||
    "";
  return String(tagged || "").trim() || null;
}

/**
 * Formato gravado no recebimento (Mailgun/legado):
 * Assunto: ...<br>Encaminhado pelo e-mail: ...<br>Mensagem: ...
 * O corpo do cliente costuma vir com quebra de linha no meio da frase
 * (ex.: "... no xpro" / "AI."). Sem isto, só o restante após a quebra aparece.
 */
function parseLabeledInboundEmail(plain) {
  const lines = String(plain || "").split("\n");
  let subject = "";
  const bodyLines = [];
  let inBody = false;
  let sawMensagem = false;

  for (const line of lines) {
    if (INBOUND_FOOTER_RE.test(line)) break;
    if (!inBody && INBOUND_ASSUNTO_RE.test(line)) {
      subject = line.replace(INBOUND_ASSUNTO_RE, "$1").trim();
      continue;
    }
    if (!inBody && INBOUND_ENCAMINHADO_RE.test(line)) continue;
    if (!inBody && INBOUND_MENSAGEM_RE.test(line)) {
      sawMensagem = true;
      inBody = true;
      const rest = line.replace(INBOUND_MENSAGEM_RE, "$1");
      if (rest) bodyLines.push(rest);
      continue;
    }
    if (inBody) bodyLines.push(line);
  }

  if (!sawMensagem) return null;
  return { subject: subject || null, body: unwrapSoftLineBreaks(bodyLines.join("\n")) };
}

/** Junta quebra de linha de e-mail (~78 caracteres) para a frase voltar a ser uma só. */
function unwrapSoftLineBreaks(text) {
  const lines = String(text || "").split("\n");
  const out = [];
  for (const line of lines) {
    const prev = out[out.length - 1];
    const current = line.trim();
    if (
      prev &&
      prev.trim() &&
      current &&
      !/[.!?:;]$/.test(prev.trim()) &&
      prev.length + 1 + current.length < 220
    ) {
      out[out.length - 1] = `${prev.trimEnd()} ${current}`;
    } else {
      out.push(line);
    }
  }
  return out.join("\n").trim();
}

/** Assunto e corpo da mensagem do cliente, no formato gravado pelo recebimento. */
export function parseCustomerEmail(message) {
  const taggedSubject = subjectFromMessageMeta(message);
  const plain = plainEmailText(message?.text || message?.mensagem || "");
  const labeled = parseLabeledInboundEmail(plain);
  if (labeled) {
    return { subject: taggedSubject || labeled.subject, body: labeled.body };
  }

  const stored = splitStoredEmail(plain);
  const fromStoredSubject = String(stored.subject || "").split("\n")[0].trim();
  if (fromStoredSubject) {
    const body = stored.body.startsWith(fromStoredSubject)
      ? stored.body.slice(fromStoredSubject.length).trim()
      : stored.body;
    return { subject: taggedSubject || fromStoredSubject, body };
  }

  const lines = plain.split("\n");
  const first = (lines[0] || "").trim();
  if (first && first.length <= 180 && lines.length > 1) {
    return { subject: taggedSubject || first, body: lines.slice(1).join("\n").trim() };
  }
  return { subject: taggedSubject || null, body: plain };
}

export function replySubjectFromCustomer(message) {
  const subject = parseCustomerEmail(message).subject;
  if (!subject) return "";
  return /^re:/i.test(subject) ? subject : `Re: ${subject}`;
}

const STAFF_SENDER_TYPES = new Set([
  "agent",
  "operator",
  "automation",
  "ai_agent",
  "chatbot",
  "system",
  "whatsapp_app",
  "meta_page",
]);

function isClientMessage(message) {
  if (!message || message.email?.direction === "outbound") return false;
  if (String(message.type || "") === "log") return false;
  const sender = String(message.sender || "").toLowerCase();
  const senderType = String(message.senderType || "").toLowerCase();
  if (STAFF_SENDER_TYPES.has(senderType) || STAFF_SENDER_TYPES.has(sender)) return false;
  if (sender === "agent" || sender === "operator" || sender === "system") return false;
  return (
    sender === "client" ||
    sender === "cliente" ||
    sender === "customer" ||
    senderType === "client" ||
    senderType === "customer" ||
    message.channel === "customer"
  );
}

function hasClientContent(message) {
  if (String(message?.text || message?.mensagem || "").trim()) return true;
  return Boolean(message?.image || message?.video || message?.audio || message?.document || message?.file);
}

export function isInboundClientEmail(message) {
  if (!isClientMessage(message)) return false;
  const origin = Number(message.originTipo);
  const channel = `${message.channel || ""} ${message.channelName || ""}`;
  return EMAIL_ORIGINS.has(origin) || /email/i.test(channel);
}

export function describeClientMessage(message) {
  const channel = String(message?.channelName || message?.channel || "").trim();
  const media = message?.image
    ? "Imagem"
    : message?.video
      ? "Vídeo"
      : message?.audio
        ? "Áudio"
        : message?.document
          ? (typeof message.document === "object" ? message.document.name || "Documento" : "Documento")
          : "";
  if (isInboundClientEmail(message)) {
    const parsed = parseCustomerEmail(message);
    return { channel, subject: parsed.subject, body: parsed.body || media };
  }
  const body = plainEmailText(message?.text || message?.mensagem || "");
  return { channel, subject: null, body: body || media };
}

export function listCustomerEmails(messages) {
  const candidates = listChatMessages(messages).filter(
    (message) => isClientMessage(message) && hasClientContent(message)
  );
  candidates.sort((a, b) => {
    const idDelta = Number(b.id) - Number(a.id);
    if (Number.isFinite(idDelta) && idDelta !== 0) return idDelta;
    return String(b.createdAt || "").localeCompare(String(a.createdAt || ""));
  });
  return candidates;
}

export function findCustomerEmailToReply(messages) {
  return listCustomerEmails(messages)[0] || null;
}

export function formatCustomerEmailWhen(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const date = new Date(raw.includes("T") ? raw : raw.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
